import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { TranslationKeys } from "../../../i18n/types";
import { type CategoricalColor, categoricalColorAt } from "../../../utils/categoricalColors";
import { isAutoConvertedIsoDateString } from "../../../utils/dateUtils";
import {
	type CalculatedDayPart,
	HOLIDAY_DAY_PARTS,
	type Holiday,
	HolidayDayPart,
	type HolidayFirstPart,
	type HolidayGroup,
	type HolidayLastPart,
	HolidayPart,
	type HolidayPerson,
	HolidayStatus,
} from "./holidayApi";

/** Stands for "no value" in filters and selects: Not assigned (group) or No day part. */
export const HOLIDAY_NONE = "none";

export const DAY_PART_LABEL_KEY: Record<HolidayDayPart, TranslationKeys> = {
	[HolidayDayPart.Day]: "holidays.dayPart.day",
	[HolidayDayPart.Evening]: "holidays.dayPart.evening",
	[HolidayDayPart.Night]: "holidays.dayPart.night",
};
export const NO_DAY_PART_KEY: TranslationKeys = "holidays.dayPart.none";

/** The day part as the changelog stores it. "Calculated" means it comes from the shifts they work most. */
const LOGGED_DAY_PART_KEYS: Record<string, TranslationKeys> = {
	Day: "holidays.dayPart.day",
	Evening: "holidays.dayPart.evening",
	Night: "holidays.dayPart.night",
	Calculated: "holidays.profile.calculatedTag",
};

/** A day part as a changelog row stores it ("Night", "Calculated"), in words. */
export const loggedDayPartLabel = (t: TFunction, dayPart: string): string => {
	const key = LOGGED_DAY_PART_KEYS[dayPart];
	// DEFAULT: a day part this screen doesn't know yet still shows, as stored, rather than hiding the change.
	return key === undefined ? dayPart : t(key);
};

/** Short names for the "Date & day parts" column: "Evening before to Night". */
export const PART_SHORT_LABEL_KEY: Record<HolidayPart, TranslationKeys> = {
	[HolidayPart.EveningBefore]: "holidays.part.short.evening_before",
	[HolidayPart.NightBefore]: "holidays.part.short.night_before",
	[HolidayPart.Day]: "holidays.part.short.day",
	[HolidayPart.Evening]: "holidays.part.short.evening",
	[HolidayPart.Night]: "holidays.part.short.night",
	[HolidayPart.NextDay]: "holidays.part.short.next_day",
};

/** The shift each part is: drives the day part glyph next to it. */
export const PART_DAY_PART: Record<HolidayPart, HolidayDayPart> = {
	[HolidayPart.EveningBefore]: HolidayDayPart.Evening,
	[HolidayPart.NightBefore]: HolidayDayPart.Night,
	[HolidayPart.Day]: HolidayDayPart.Day,
	[HolidayPart.Evening]: HolidayDayPart.Evening,
	[HolidayPart.Night]: HolidayDayPart.Night,
	[HolidayPart.NextDay]: HolidayDayPart.Day,
};

export const FIRST_PART_LABEL_KEY: Record<HolidayFirstPart, TranslationKeys> = {
	[HolidayPart.EveningBefore]: "holidays.part.first.evening_before",
	[HolidayPart.NightBefore]: "holidays.part.first.night_before",
	[HolidayPart.Day]: "holidays.part.first.day",
	[HolidayPart.Evening]: "holidays.part.first.evening",
};

export const LAST_PART_LABEL_KEY: Record<HolidayLastPart, TranslationKeys> = {
	[HolidayPart.Evening]: "holidays.part.last.evening",
	[HolidayPart.Night]: "holidays.part.last.night",
	[HolidayPart.NextDay]: "holidays.part.last.next_day",
};

/** One part when the holiday starts and ends with the same shift, otherwise the first and the last. */
export const holidayPartsRange = (holiday: Pick<Holiday, "firstPart" | "lastPart">): HolidayPart[] =>
	holiday.firstPart === holiday.lastPart ? [holiday.firstPart] : [holiday.firstPart, holiday.lastPart];

/** The status column: a holiday with no group yet asks for one. */
export const holidayStatusKey = (holiday: Pick<Holiday, "selectedGroupId" | "status">): TranslationKeys => {
	if (holiday.selectedGroupId === null && holiday.status === HolidayStatus.NotApplied) return "holidays.status.chooseGroup";
	const keys: Record<HolidayStatus, TranslationKeys> = {
		[HolidayStatus.NotApplied]: "holidays.status.notApplied",
		[HolidayStatus.Applied]: "holidays.status.applied",
		[HolidayStatus.Changed]: "holidays.status.changed",
	};
	return keys[holiday.status];
};

/** Why a holiday can't be ticked for Apply, or null when it can. */
export const applyBlockedKey = (holiday: Pick<Holiday, "selectedGroupId" | "status" | "canChangeGroup">): TranslationKeys | null => {
	if (holiday.status === HolidayStatus.Applied) return "holidays.apply.blocked.applied";
	if (holiday.selectedGroupId === null) return "holidays.apply.blocked.noGroup";
	// Once its hours start, the holiday is being worked and can't change.
	if (!holiday.canChangeGroup) return "holidays.apply.blocked.started";
	return null;
};

/** "Fri, Dec 25, 2026" */
export const formatHolidayDate = (date: DateTime): string => date.toFormat("ccc, MMM d, yyyy");

/** "07:00" → "7:00 AM" */
export const formatClockTime = (clock: string): string => DateTime.fromFormat(clock, "HH:mm").toFormat("h:mm a");

/** Whether a day part ends on the next day: an end at or before its start. */
export const endsNextDay = (start: string, end: string): boolean => end < start;

/** "7:00 AM–7:30 PM", or null when the person had no shifts in the last 6 weeks. */
export const mostFrequentShiftText = (calculated: CalculatedDayPart): string | null =>
	calculated.start === null || calculated.end === null ? null : `${formatClockTime(calculated.start)}–${formatClockTime(calculated.end)}`;

/** A group's colour comes from its position, so A is always the first categorical colour. */
export const holidayGroupColor = (groups: readonly HolidayGroup[], groupId: string): CategoricalColor => {
	const index = groups.findIndex((group) => group.id === groupId);
	if (index < 0) throw new Error(`Holiday group ${groupId} is not one of the department's groups`);
	return categoricalColorAt(index);
};

export const holidayGroupById = (groups: readonly HolidayGroup[], groupId: string): HolidayGroup | undefined => groups.find((group) => group.id === groupId);

/** "Group A" */
export const holidayGroupLabel = (t: TFunction, group: HolidayGroup): string => t("holidays.group.name", { name: group.name });

/** "Maria, James, Priya and 4 more" — the first three names, then a count. */
export const NAMES_SHOWN = 3;
export const summarizeNames = (t: TFunction, names: readonly string[]): string => {
	if (names.length <= NAMES_SHOWN) return names.join(", ");
	return t("holidays.groupChange.andMore", { names: names.slice(0, NAMES_SHOWN).join(", "), count: names.length - NAMES_SHOWN });
};

/** Whether a person in `groupId` works the holiday. A holiday with no group yet is Off for everyone. */
export const worksHoliday = (holiday: Pick<Holiday, "selectedGroupId">, groupId: string): boolean => holiday.selectedGroupId === groupId;

/** A name starting like "2026-12-25". */
const STARTS_LIKE_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * Whether a holiday name would come back from the server as a date instead of text. The server turns away
 * any name that starts like a date — wider than what the app converts — so both are checked.
 */
export const holidayNameLooksLikeDate = (name: string): boolean => {
	const trimmed = name.trim();
	return isAutoConvertedIsoDateString(trimmed) || STARTS_LIKE_DATE.test(trimmed);
};

/** A balance row: a day part, or the grouped people with no shifts in the last 6 weeks. */
export type BalanceRowKey = HolidayDayPart | typeof HOLIDAY_NONE;
export const BALANCE_ROWS: readonly BalanceRowKey[] = [...HOLIDAY_DAY_PARTS, HOLIDAY_NONE];

export type GroupBalance = {
	/** groupId → row → people. */
	counts: ReadonlyMap<string, Readonly<Record<BalanceRowKey, number>>>;
	/** The largest cell of each row, to draw each cell against. */
	rowMax: Readonly<Record<BalanceRowKey, number>>;
	/** Everyone counted, across the whole table. */
	total: number;
};

const emptyRow = (): Record<BalanceRowKey, number> => ({ day: 0, evening: 0, night: 0, [HOLIDAY_NONE]: 0 });

/**
 * Grouped people per group and day part, optionally only those holding `jobName`. People come from the
 * active list, so deactivated people are never counted.
 */
export const computeGroupBalance = (
	people: readonly HolidayPerson[],
	groups: readonly HolidayGroup[],
	jobName: string | null,
	jobNamesByNurseId: ReadonlyMap<number, readonly string[]>,
): GroupBalance => {
	const counts = new Map<string, Record<BalanceRowKey, number>>(groups.map((group) => [group.id, emptyRow()]));
	let total = 0;
	for (const person of people) {
		if (person.groupId === null) continue;
		const row = counts.get(person.groupId);
		// A group the settings don't list (removed in another tab) isn't a column to count into.
		if (row === undefined) continue;
		if (jobName !== null && !jobNamesByNurseId.get(person.nurseId)?.includes(jobName)) continue;
		row[person.dayPart ?? HOLIDAY_NONE] += 1;
		total += 1;
	}
	const rowMax = emptyRow();
	for (const row of counts.values()) for (const key of BALANCE_ROWS) rowMax[key] = Math.max(rowMax[key], row[key]);
	return { counts, rowMax, total };
};

/** People per group, plus the ones not assigned — the hero's figures. */
export const countPeopleByGroup = (people: readonly HolidayPerson[]): { byGroupId: ReadonlyMap<string, number>; notAssigned: number } => {
	const byGroupId = new Map<string, number>();
	let notAssigned = 0;
	for (const person of people) {
		if (person.groupId === null) notAssigned += 1;
		// DEFAULT: the first person counted in a group starts it at 0.
		else byGroupId.set(person.groupId, (byGroupId.get(person.groupId) ?? 0) + 1);
	}
	return { byGroupId, notAssigned };
};
